import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  Grid,
} from "@mui/material";
import { VenueFormData, RoomData } from "../../../types/venue";
import patternUrl from "../../../assets/pattern.png";
import React, { useMemo, useEffect, useState, useRef } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";
import PenPaperIcon from "../../../components/icons/PenPaperIcon";
import { TrashIcon } from "../../../components/icons";

interface VenuePreviewProps {
  formData: VenueFormData;
  currentStep: number;
  onRoomsChange?: (rooms: RoomData[]) => void;
  onStepChange: (step: number) => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
  setEditingContactId: (id: string | null) => void;
}

// ── constants ──────────────────────────────────────────────────────────────

const FLOOR_PLAN_TYPES = [
  "image/",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const isAllowedFloorPlanType = (file: File) =>
  FLOOR_PLAN_TYPES.some((t) =>
    t.endsWith("/") ? file.type.startsWith(t) : file.type === t,
  );

// ── lightbox state type ────────────────────────────────────────────────────

type LightboxState = {
  name: string;
  isPdf?: boolean;
  isDocx?: boolean;
  // room-level
  roomId?: string;
  index?: number;
  type?: "photo" | "floorplan";
  // venue-level
  venueCategory?: "venue" | "floorPlan";
  venueIndex?: number;
};

// ── file badge (PDF / DOCX) ────────────────────────────────────────────────

function FileBadge({
  name,
  isPdf,
  isDocx,
  size = 80,
}: {
  name: string;
  isPdf?: boolean;
  isDocx?: boolean;
  size?: number;
}) {
  const label = isDocx
    ? "DOCX"
    : isPdf
      ? "PDF"
      : (name.split(".").pop()?.toUpperCase() ?? "FILE");
  const bg = isDocx ? "#e8f0fe" : "#f9f5f3";
  const color = isDocx ? "#1a56db" : "#8B4513";

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        backgroundColor: bg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        overflow: "hidden",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color,
          fontWeight: 700,
          fontSize: size < 80 ? "0.6rem" : "0.75rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{
          fontSize: "0.55rem",
          textAlign: "center",
          px: 0.5,
          wordBreak: "break-all",
          lineHeight: 1.2,
        }}
      >
        {name.length > 12 ? name.slice(0, 12) + "…" : name}
      </Typography>
    </Box>
  );
}

// ── hoverable thumbnail ────────────────────────────────────────────────────

function HoverThumb({
  children,
  onDelete,
  onClick,
}: {
  children: React.ReactNode;
  onDelete: () => void;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      sx={{ position: "relative", display: "inline-flex", cursor: "pointer" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {children}

      {/* Overlay on hover */}
      {hovered && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: 2,
            backgroundColor: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        />
      )}

      {/* Delete button */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        sx={{
          position: "absolute",
          top: 3,
          right: 3,
          zIndex: 3,
          p: 0.3,
          backgroundColor: "rgba(200,0,0,0.75)",
          color: "#fff",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.15s",
          "&:hover": { backgroundColor: "rgba(200,0,0,1)" },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

// ── component ──────────────────────────────────────────────────────────────

export default function VenuePreview({
  formData,
  currentStep,
  onStepChange,
  onRoomsChange,
  setFormData,
  setEditingContactId,
}: VenuePreviewProps) {
  const contacts = formData.contacts ?? [];

  const TOTAL = 101;

  const calculateCompleteness = () => {
    let score = 0;
    if (formData.venueName) score += 10;
    if (formData.addressOne) score += 8;
    if (formData.addressTwo) score += 3;
    if (formData.suburb) score += 4;
    if (formData.city) score += 4;
    if (formData.capacity) score += 4;
    if (formData.venueType) score += 3;
    if (formData.notes) score += 2;
    if ((formData.contacts_ids ?? []).length > 0) score += 3;
    score += 3; // time_zone
    score += 3; // country
    score += 1; // created_by
    score += 1; // last_updated_by
    if (formData.doesVenueHavePower === "yes") score += 3;
    if (formData.powerType) score += 2;
    if (formData.powerOutageRate) score += 2;
    if (formData.powerSocketType) score += 2;
    if (formData.powerBackup) score += 2;
    if (formData.backupPowerCapacity) score += 2;
    if (formData.power_distance_from_livestream_desk) score += 2;
    if (formData.internet_service_provider) score += 3;
    if (formData.wifi_name) score += 3;
    if (formData.wifi_password) score += 3;
    if (formData.internet_upload_speed) score += 3;
    if (formData.router_accessibility) score += 2;
    if (formData.router_distance_from_livestream) score += 2;
    if (formData.has_pa_system === "yes") score += 3;
    if (formData.pa_system_distance_from_livestream) score += 2;
    if (formData.pa_system_provider) score += 2;
    if (formData.other_pa_system_service_provider) score += 2;
    if (formData.pa_system_contact_name) score += 2;
    if (formData.pa_system_contact_phone) score += 2;
    if (formData.pa_system_contact_email) score += 2;
    if ((formData.uploadedFiles?.venue ?? []).length > 0) score += 3;
    if ((formData.uploadedFiles?.floorPlan ?? []).length > 0) score += 3;
    return Math.round((score / TOTAL) * 100);
  };

  const completeness = formData.completeness_score ?? calculateCompleteness();
  const scoreColor =
    completeness >= 80 ? "#4CAF50" : completeness >= 50 ? "#FF9800" : "#f44336";
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  // ── Stable URL cache — same File object always gets the same blob URL ──────
  const urlCache = useRef<WeakMap<File, string>>(new WeakMap());
  const allFiles = useRef<Set<File>>(new Set());

  const getUrl = (file: File): string => {
    if (!urlCache.current.has(file)) {
      urlCache.current.set(file, URL.createObjectURL(file));
      allFiles.current.add(file);
    }
    return urlCache.current.get(file)!;
  };

  // ── URL memo helpers ───────────────────────────────────────────────────────

  const venuePhotoUrls = useMemo(() => {
    return (formData.uploadedFiles?.venue ?? [])
      .filter((f) => f instanceof File && f.type.startsWith("image/"))
      .map((f) => getUrl(f));
  }, [formData.uploadedFiles?.venue]);

  const venueFloorplanUrls = useMemo(() => {
    return (formData.uploadedFiles?.floorPlan ?? [])
      .filter((f) => f instanceof File && isAllowedFloorPlanType(f))
      .map((f) => ({
        url: f.type.startsWith("image/") ? getUrl(f) : "",
        isPdf: f.type === "application/pdf",
        isDocx: f.type.includes("wordprocessingml"),
        name: f.name,
      }));
  }, [formData.uploadedFiles?.floorPlan]);

  const roomPhotoUrls = useMemo(() => {
    const map: Record<string, string[]> = {};
    (formData.rooms ?? []).forEach((room) => {
      map[room.id] = (room.photos ?? [])
        .filter((f) => f instanceof File && f.type.startsWith("image/"))
        .map((f) => getUrl(f));
    });
    return map;
  }, [formData.rooms]);

  const roomFloorplanUrls = useMemo(() => {
    const map: Record<
      string,
      { url: string; isPdf: boolean; isDocx: boolean; name: string }[]
    > = {};
    (formData.rooms ?? []).forEach((room) => {
      map[room.id] = (room.floorplans ?? [])
        .filter((f) => f instanceof File && isAllowedFloorPlanType(f))
        .map((f) => ({
          url: f.type.startsWith("image/") ? getUrl(f) : "",
          isPdf: f.type === "application/pdf",
          isDocx: f.type.includes("wordprocessingml"),
          name: f.name,
        }));
    });
    return map;
  }, [formData.rooms]);

  // ── Derive lightbox URL fresh from current memos (never stale) ────────────
  const lightboxUrl = useMemo(() => {
    if (!lightbox) return "";
    if (
      lightbox.venueCategory === "venue" &&
      lightbox.venueIndex !== undefined
    ) {
      return venuePhotoUrls[lightbox.venueIndex] ?? "";
    }
    if (
      lightbox.venueCategory === "floorPlan" &&
      lightbox.venueIndex !== undefined
    ) {
      return venueFloorplanUrls[lightbox.venueIndex]?.url ?? "";
    }
    if (lightbox.roomId && lightbox.index !== undefined) {
      if (lightbox.type === "photo")
        return roomPhotoUrls[lightbox.roomId]?.[lightbox.index] ?? "";
      if (lightbox.type === "floorplan")
        return roomFloorplanUrls[lightbox.roomId]?.[lightbox.index]?.url ?? "";
    }
    return "";
  }, [
    lightbox,
    venuePhotoUrls,
    venueFloorplanUrls,
    roomPhotoUrls,
    roomFloorplanUrls,
  ]);

  // ── Cleanup — revoke all blob URLs on unmount ──────────────────────────────
  useEffect(() => {
    const files = allFiles.current;
    const cache = urlCache.current;
    return () => {
      files.forEach((f) => {
        const url = cache.get(f);
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  // ── Delete helpers ─────────────────────────────────────────────────────────

  /** Delete a venue-level photo or floorplan by index */
  const deleteVenueFile = (category: "venue" | "floorPlan", index: number) => {
    setFormData((prev) => ({
      ...prev,
      uploadedFiles: {
        ...prev.uploadedFiles,
        [category]: (prev.uploadedFiles[category] ?? []).filter(
          (_, i) => i !== index,
        ),
      },
    }));
    setLightbox(null);
  };

  /** Delete a room-level photo or floorplan */
  const deleteRoomFile = (
    roomId: string,
    index: number,
    type: "photo" | "floorplan",
  ) => {
    if (!onRoomsChange) return;
    const updated = (formData.rooms ?? []).map((r) => {
      if (r.id !== roomId) return r;
      if (type === "photo")
        return { ...r, photos: r.photos.filter((_, i) => i !== index) };
      return { ...r, floorplans: r.floorplans.filter((_, i) => i !== index) };
    });
    onRoomsChange(updated);
    setLightbox(null);
  };

  /** Unified delete from lightbox */
  const deleteLightboxFile = () => {
    if (!lightbox) return;
    if (
      lightbox.venueCategory !== undefined &&
      lightbox.venueIndex !== undefined
    ) {
      deleteVenueFile(lightbox.venueCategory, lightbox.venueIndex);
    } else if (
      lightbox.roomId &&
      lightbox.index !== undefined &&
      lightbox.type
    ) {
      deleteRoomFile(lightbox.roomId, lightbox.index, lightbox.type);
    }
  };

  // ── Room helpers ───────────────────────────────────────────────────────────

  const deleteRoom = (roomId: string) => {
    if (!onRoomsChange) return;
    onRoomsChange((formData.rooms ?? []).filter((r) => r.id !== roomId));
  };

  const scrollToRoom = (roomId: string) => {
    const el = document.getElementById(`room-accordion-${roomId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ── Address / power helpers ────────────────────────────────────────────────

  const getFullAddress = () => {
    const parts = [
      formData.addressOne,
      formData.addressTwo,
      formData.suburb,
      formData.city,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const getBackupPowerSummary = () => {
    if (!formData.powerBackup || !formData.backupPowerCapacity) return [];
    return formData.powerBackup
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .map((type) => {
        const entry = formData
          .backupPowerCapacity!.split(",")
          .find((e) => e.startsWith(`${type}:`));
        const capacity = entry ? entry.split(":")[1]?.trim() || "0" : "0";
        return `${type} - ${capacity} kw`;
      });
  };

  const getSocketTypes = () => {
    if (!formData.powerSocketType) return [];
    return formData.powerSocketType
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  };

  const address = getFullAddress();
  const backupPower = getBackupPowerSummary();
  const socketTypes = getSocketTypes();

  // ── Field groups ───────────────────────────────────────────────────────────

  const venueFields = [
    formData.venueName && { label: "Venue Name", value: formData.venueName },
    formData.venueType && { label: "Venue Type", value: formData.venueType },
    formData.capacity && { label: "Capacity", value: formData.capacity },
    address && { label: "Address", value: address },
    formData.notes && { label: "Notes", value: formData.notes },
  ].filter(Boolean) as { label: string; value: string }[];

  const powerFields = [
    formData.doesVenueHavePower && {
      label: "Has Power",
      value: formData.doesVenueHavePower === "yes" ? "Yes" : "No",
    },
    formData.powerType && { label: "Power Type", value: formData.powerType },
    formData.powerOutageRate && {
      label: "Power Outage Rate",
      value: formData.powerOutageRate,
    },
    formData.power_distance_from_livestream_desk && {
      label: "Power → Livestream Desk",
      value: `${formData.power_distance_from_livestream_desk} m`,
    },
  ].filter(Boolean) as { label: string; value: string }[];

  const internetFields = [
    formData.internet_service_provider && {
      label: "Provider",
      value: formData.internet_service_provider,
    },
    formData.wifi_name && { label: "Wi-Fi Name", value: formData.wifi_name },
    formData.internet_upload_speed && {
      label: "Upload Speed",
      value: `${formData.internet_upload_speed} Mbps`,
    },
    formData.router_distance_from_livestream && {
      label: "Router → Livestream Desk",
      value: `${formData.router_distance_from_livestream} m`,
    },
    formData.router_accessibility && {
      label: "Router Accessible",
      value: formData.router_accessibility === "yes" ? "Yes" : "No",
    },
  ].filter(Boolean) as { label: string; value: string }[];

  const paFields = [
    formData.has_pa_system && {
      label: "Has PA System",
      value: formData.has_pa_system === "yes" ? "Yes" : "No",
    },
    formData.pa_system_provider && {
      label: "PA Provider",
      value: formData.pa_system_provider,
    },
    formData.other_pa_system_service_provider && {
      label: "Other PA Provider",
      value: formData.other_pa_system_service_provider,
    },
    formData.pa_system_distance_from_livestream && {
      label: "PA → Livestream Desk",
      value: `${formData.pa_system_distance_from_livestream} m`,
    },
    formData.pa_system_contact_name && {
      label: "PA Contact Name",
      value: formData.pa_system_contact_name,
    },
    formData.pa_system_contact_phone && {
      label: "PA Contact Phone",
      value: formData.pa_system_contact_phone,
    },
    formData.pa_system_contact_email && {
      label: "PA Contact Email",
      value: formData.pa_system_contact_email,
    },
  ].filter(Boolean) as { label: string; value: string }[];

  const rooms = (formData.rooms ?? []).filter(
    (r) => r.name || r.capacity || r.room_type,
  );

  const color100 = reduxTheme.primary_colors?.[100];
  const color700 = reduxTheme.primary_colors?.[700];
  const color300 = reduxTheme.primary_colors?.[300];

  return (
    <Paper
      sx={{
        p: 0,
        borderRadius: 3,
        overflow: "hidden",
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      }}
    >
      {/* ── Pattern Header ── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: 120,
          p: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          backgroundImage: `linear-gradient(135deg, #6B3A2A 0%, #C4856A 50%, #6B3A2A 100%), url(${patternUrl})`,
          backgroundSize: "cover, 250%",
          backgroundPosition: "center, center",
          backgroundBlendMode: "multiply",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: reduxTheme.primary_colors?.[900],
                mb: 0.5,
                fontSize: 18,
              }}
            >
              Completeness score
            </Typography>
          </Box>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={completeness}
              size={60}
              thickness={4}
              sx={{
                color: scoreColor,
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "50%",
              }}
            />
            <Box
              sx={{
                inset: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: reduxTheme.primary_colors?.[900] }}
              >
                {completeness}%
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* ── Content ── */}
      <Box sx={{ flexGrow: 1, overflow: "auto", px: 3, pb: 3, pt: 2 }}>
        {/* ── Contacts ── */}
        {currentStep >= 5 && contacts.length > 0 && (
          <>
            <SectionLabel label="Contacts" />
            {contacts.map((contact) => (
              <Box
                key={contact.id}
                sx={{
                  position: "relative",
                  backgroundColor: "transparent",
                  borderRadius: 4,
                  border: `1px solid ${reduxTheme.primary_colors?.[800]}`,
                  mt: 3,
                  mb: 2,
                  p: 3,
                }}
              >
                {/* ── Action Icons (Floating on top right border) ── */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -11, // Adjusted based on height 22 to sit exactly on the line
                    right: 16,
                    display: "flex",
                    gap: 1,
                    zIndex: 1, // Ensures icons stay above the border
                  }}
                >
                  {/* ── Delete Button ── */}
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        contacts: (prev.contacts ?? []).filter(
                          (c) => c.id !== contact.id,
                        ),
                        contacts_ids: (prev.contacts_ids ?? []).filter(
                          (id) => id !== Number(contact.id),
                        ),
                      }))
                    }
                    sx={{
                      backgroundColor: reduxTheme.primary_colors?.[900],

                      borderRadius: 1.5,
                      "&:hover": { backgroundColor: "#555" },
                      width: 22,
                      height: 22,
                      padding: 0,
                    }}
                  >
                    <TrashIcon style={{ width: 14, height: 14 }} />
                  </IconButton>

                  {/* ── Edit Button ── */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingContactId(contact.id);
                      onStepChange(5);
                    }}
                    sx={{
                      backgroundColor: reduxTheme.primary_colors?.[900],

                      borderRadius: 1.5,

                      width: 22,
                      height: 22,
                      padding: 0,
                    }}
                  >
                    <PenPaperIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>

                {/* ── Contact Info Table-like Layout ── */}
                <Grid container spacing={1}>
                  {[
                    {
                      label: "Name",
                      value: `${contact.name} ${contact.surname}`.trim(),
                    },
                    { label: "Role", value: contact.role },
                    { label: "Phone", value: contact.phone },
                    { label: "Email", value: contact.email },
                  ].map(
                    (row, idx) =>
                      row.value && (
                        <React.Fragment key={idx}>
                          <Grid size={{ xs: 3 }}>
                            <Typography
                              sx={{ color: color300, fontSize: "13px" }}
                            >
                              {row.label}:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 1 }}>
                            {/* For the colon alignment in your image */}
                            <Typography
                              sx={{ color: color700, fontSize: "13px" }}
                            >
                              :
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography
                              sx={{
                                color: color100,
                                fontSize: "13px",
                                fontWeight: 500,
                                wordBreak: "break-all",
                                overflowWrap: "anywhere",
                                whiteSpace: "normal",
                              }}
                            >
                              {row.value}
                            </Typography>
                          </Grid>
                        </React.Fragment>
                      ),
                  )}
                </Grid>
              </Box>
            ))}
          </>
        )}

        {/* ── Rooms ── */}
        {currentStep >= 4 && rooms.length > 0 && (
          <>
            {currentStep >= 5 && contacts.length > 0 && (
              <Divider sx={{ my: 2 }} />
            )}
            <SectionLabel label="Rooms" />
            {rooms.map((room, i) => (
              <Box
                key={room.id}
                sx={{
                  backgroundColor: "#e0e0e0",
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  mb: 2,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, color: "#333" }}
                  >
                    {room.name || `Room ${i + 1}`}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => deleteRoom(room.id)}
                      sx={{
                        border: "1px solid #999",
                        borderRadius: 1,
                        mr: 1,
                        p: 0.5,
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18, color: "#666" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => scrollToRoom(room.id)}
                      sx={{ border: "1px solid #999", borderRadius: 1, p: 0.5 }}
                    >
                      <PenPaperIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  {room.name && (
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography
                        sx={{ color: "#666", width: 100, fontSize: 14 }}
                      >
                        Room Name:
                      </Typography>
                      <Typography sx={{ color: "#333", fontSize: 14 }}>
                        {room.name}
                      </Typography>
                    </Box>
                  )}
                  {room.capacity && (
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography
                        sx={{ color: "#666", width: 100, fontSize: 14 }}
                      >
                        Capacity:
                      </Typography>
                      <Typography sx={{ color: "#333", fontSize: 14 }}>
                        {room.capacity}
                      </Typography>
                    </Box>
                  )}
                  {room.room_type && (
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography
                        sx={{ color: "#666", width: 100, fontSize: 14 }}
                      >
                        Room Type:
                      </Typography>
                      <Typography sx={{ color: "#333", fontSize: 14 }}>
                        {room.room_type}
                      </Typography>
                    </Box>
                  )}
                  {room.layouts.filter((l) => l.capacity > 0).length > 0 && (
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography
                        sx={{ color: "#666", width: 100, fontSize: 14 }}
                      >
                        Layouts:
                      </Typography>
                      <Box>
                        {room.layouts
                          .filter((l) => l.capacity > 0)
                          .map((l) => (
                            <Typography
                              key={l.id}
                              sx={{ color: "#333", fontSize: 14 }}
                            >
                              {l.name}: {l.capacity}
                            </Typography>
                          ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Room photos */}
                {(roomPhotoUrls[room.id] ?? []).length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
                  >
                    {(roomPhotoUrls[room.id] ?? []).map((url, pi) => (
                      <HoverThumb
                        key={pi}
                        onDelete={() => deleteRoomFile(room.id, pi, "photo")}
                        onClick={() =>
                          setLightbox({
                            name: `Room photo ${pi + 1}`,
                            roomId: room.id,
                            index: pi,
                            type: "photo",
                          })
                        }
                      >
                        <Box
                          component="img"
                          src={url}
                          alt={`Room photo ${pi + 1}`}
                          sx={{
                            width: 80,
                            height: 50,
                            objectFit: "cover",
                            borderRadius: 1,
                            backgroundColor: "#888",
                            display: "block",
                          }}
                        />
                      </HoverThumb>
                    ))}
                  </Box>
                )}

                {/* Room floorplans */}
                {(roomFloorplanUrls[room.id] ?? []).length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {(roomFloorplanUrls[room.id] ?? []).map((item, pi) => (
                      <HoverThumb
                        key={pi}
                        onDelete={() =>
                          deleteRoomFile(room.id, pi, "floorplan")
                        }
                        onClick={() =>
                          setLightbox({
                            name: item.name,
                            isPdf: item.isPdf,
                            isDocx: item.isDocx,
                            roomId: room.id,
                            index: pi,
                            type: "floorplan",
                          })
                        }
                      >
                        {item.url ? (
                          <Box
                            component="img"
                            src={item.url}
                            alt={`Floorplan ${pi + 1}`}
                            sx={{
                              width: 80,
                              height: 50,
                              objectFit: "cover",
                              borderRadius: 1,
                              backgroundColor: "#888",
                              display: "block",
                            }}
                          />
                        ) : (
                          <FileBadge
                            name={item.name}
                            isPdf={item.isPdf}
                            isDocx={item.isDocx}
                            size={80}
                          />
                        )}
                      </HoverThumb>
                    ))}
                  </Box>
                )}

                {(roomPhotoUrls[room.id] ?? []).length === 0 &&
                  (roomFloorplanUrls[room.id] ?? []).length === 0 && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {[0, 1, 2].map((n) => (
                        <Box
                          key={n}
                          sx={{
                            width: 80,
                            height: 50,
                            backgroundColor: "#888",
                            borderRadius: 1,
                          }}
                        />
                      ))}
                    </Box>
                  )}
              </Box>
            ))}
          </>
        )}

        {/* ── PA System ── */}
        {currentStep >= 3 && paFields.length > 0 && (
          <>
            {((currentStep >= 4 && rooms.length > 0) ||
              (currentStep >= 5 && contacts.length > 0)) && (
              <Divider sx={{ my: 2 }} />
            )}
            <SectionLabel label="PA System" />
            {paFields.map((f) => (
              <PreviewRow key={f.label} label={f.label} value={f.value} />
            ))}
          </>
        )}

        {/* ── Internet ── */}
        {currentStep >= 2 && internetFields.length > 0 && (
          <>
            {((currentStep >= 3 && paFields.length > 0) ||
              (currentStep >= 4 && rooms.length > 0) ||
              (currentStep >= 5 && contacts.length > 0)) && (
              <Divider sx={{ my: 2 }} />
            )}
            <SectionLabel label="Internet" />
            {internetFields.map((f) => (
              <PreviewRow key={f.label} label={f.label} value={f.value} />
            ))}
          </>
        )}

        {/* ── Power ── */}
        {currentStep >= 1 && powerFields.length > 0 && (
          <>
            {((currentStep >= 2 && internetFields.length > 0) ||
              (currentStep >= 3 && paFields.length > 0) ||
              (currentStep >= 4 && rooms.length > 0) ||
              (currentStep >= 5 && contacts.length > 0)) && (
              <Divider sx={{ my: 2 }} />
            )}
            <SectionLabel label="Power" />
            {powerFields.map((f) => (
              <PreviewRow key={f.label} label={f.label} value={f.value} />
            ))}
            {backupPower.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="textSecondary">
                  Backup Power
                </Typography>
                {backupPower.map((item, i) => (
                  <Typography key={i} variant="body1" fontWeight={500}>
                    {item}
                  </Typography>
                ))}
              </Box>
            )}
            {socketTypes.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="textSecondary">
                  Socket Types
                </Typography>
                {socketTypes.map((t, i) => (
                  <Typography key={i} variant="body1" fontWeight={500}>
                    {t}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}

        {/* ── Venue Essentials ── */}
        {currentStep >= 0 && venueFields.length > 0 && (
          <>
            {(venueFields.length > 0 ||
              venuePhotoUrls.length > 0 ||
              venueFloorplanUrls.length > 0) && (
              <>
                {currentStep > 0 && <Divider sx={{ my: 2 }} />}
                <SectionLabel label="Venue Essentials" />
                {venueFields.map((f) => (
                  <PreviewRow key={f.label} label={f.label} value={f.value} />
                ))}

                {/* Venue photos */}
                {venuePhotoUrls.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    {venuePhotoUrls.map((url, i) => (
                      <HoverThumb
                        key={i}
                        onDelete={() => deleteVenueFile("venue", i)}
                        onClick={() =>
                          setLightbox({
                            name: `Venue photo ${i + 1}`,
                            venueCategory: "venue",
                            venueIndex: i,
                          })
                        }
                      >
                        <Box
                          component="img"
                          src={url}
                          alt={`Venue photo ${i + 1}`}
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                            display: "block",
                          }}
                        />
                      </HoverThumb>
                    ))}
                  </Box>
                )}

                {/* Venue floorplans */}
                {venueFloorplanUrls.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    {venueFloorplanUrls.map((item, i) => (
                      <HoverThumb
                        key={i}
                        onDelete={() => deleteVenueFile("floorPlan", i)}
                        onClick={() =>
                          setLightbox({
                            name: item.name,
                            isPdf: item.isPdf,
                            isDocx: item.isDocx,
                            venueCategory: "floorPlan",
                            venueIndex: i,
                          })
                        }
                      >
                        {item.url ? (
                          <Box
                            component="img"
                            src={item.url}
                            alt={`Floor plan ${i + 1}`}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "1px solid #e0e0e0",
                              display: "block",
                            }}
                          />
                        ) : (
                          <FileBadge
                            name={item.name}
                            isPdf={item.isPdf}
                            isDocx={item.isDocx}
                            size={80}
                          />
                        )}
                      </HoverThumb>
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {/* Empty state */}
        {venueFields.length === 0 &&
          powerFields.length === 0 &&
          internetFields.length === 0 &&
          paFields.length === 0 &&
          rooms.length === 0 &&
          contacts.length === 0 && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body2" color="textSecondary">
                Start filling in details to see a preview here.
              </Typography>
            </Box>
          )}
      </Box>

      {/* ── Lightbox Dialog ── */}
      <Dialog
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#000", borderRadius: 3 } }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          {/* Close */}
          <IconButton
            onClick={() => setLightbox(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Delete */}
          <IconButton
            onClick={deleteLightboxFile}
            sx={{
              position: "absolute",
              top: 8,
              right: 56,
              zIndex: 10,
              backgroundColor: "rgba(200,0,0,0.7)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(200,0,0,1)" },
            }}
          >
            <DeleteForeverIcon />
          </IconButton>

          {/* Content */}
          {lightbox?.isPdf || lightbox?.isDocx ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 6,
              }}
            >
              <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>
                {lightbox.isDocx ? "Word Document" : "PDF File"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}
              >
                {lightbox.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.5)" }}
              >
                Preview not available — use the delete button above to remove
                this file.
              </Typography>
            </Box>
          ) : (
            <Box
              component="img"
              src={lightboxUrl}
              alt="Full preview"
              sx={{
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

// ── helper components ──────────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        mb: 1,
        display: "block",
      }}
    >
      {label}
    </Typography>
  );
}

function PreviewRow({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) {
  return (
    <Box sx={{ display: "flex", mb: 1 }}>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ width: 110, fontSize: 14, flexShrink: 0 }}
      >
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}
